import type { FeatureResponse } from './client';

export interface PointInTimeEarnings {
  seconds: number;
  value: number;
}

/**
 * Compute the accumulated earnings chart data from features and current simulation time.
 *
 * The chart is piecewise-linear: the slope increases each time a feature completes.
 * We only emit points at event boundaries (t=0, each completion time, currentTimeS),
 * computing exact values analytically between events.
 */
export function computeEarningsChart(features: FeatureResponse[], currentTimeS: number): PointInTimeEarnings[] {
  if (currentTimeS <= 0) {
    return [{ seconds: 0, value: 0 }];
  }

  const events = features
    .filter((f) => f.status === 'done' && f.completed_at_s != null && f.completed_at_s! < currentTimeS)
    .map((f) => ({ time: f.completed_at_s!, rate: f.earnings_rate }))
    .sort((a, b) => a.time - b.time);

  const points: PointInTimeEarnings[] = [{ seconds: 0, value: 0 }];
  let cumulativeRate = 0;
  let lastTime = 0;
  let lastValue = 0;

  for (const event of events) {
    const dt = event.time - lastTime;
    lastValue += cumulativeRate * dt;
    cumulativeRate += event.rate;
    lastTime = event.time;
    points.push({ seconds: event.time, value: lastValue });
  }

  // Final point at currentTimeS
  const dtFinal = currentTimeS - lastTime;
  const finalValue = lastValue + cumulativeRate * dtFinal;
  points.push({ seconds: currentTimeS, value: finalValue });

  return points;
}

/**
 * Compute the earnings rate chart data (EUR/s step function) from features and current simulation time.
 *
 * The rate is a step function that jumps each time a feature completes.
 * We only emit points at event boundaries — two per event (before/after the jump)
 * to produce clean vertical steps in Recharts.
 */
export function computeEarningsRateChart(features: FeatureResponse[], currentTimeS: number): PointInTimeEarnings[] {
  if (currentTimeS <= 0) {
    return [{ seconds: 0, value: 0 }];
  }

  const events = features
    .filter((f) => f.status === 'done' && f.completed_at_s != null && f.completed_at_s! < currentTimeS)
    .map((f) => ({ time: f.completed_at_s!, rate: f.earnings_rate }))
    .sort((a, b) => a.time - b.time);

  const points: PointInTimeEarnings[] = [{ seconds: 0, value: 0 }];
  let cumulativeRate = 0;

  for (const event of events) {
    // Point just before the jump (same time, old rate) — only if rate was different
    if (points[points.length - 1].seconds !== event.time) {
      points.push({ seconds: event.time, value: cumulativeRate });
    }
    cumulativeRate += event.rate;
    // Point at the jump (same time, new rate)
    points.push({ seconds: event.time, value: cumulativeRate });
  }

  // Final point at currentTimeS
  points.push({ seconds: currentTimeS, value: cumulativeRate });

  return points;
}

/**
 * Compute simulation results for a specific team.
 */
export function computeResults(
  features: FeatureResponse[],
  teamName: string,
  currentTimeS: number,
): {
  tasksCompleted: number;
  featuresCompleted: number;
  earned: number;
  earningsPerSecond: number;
} {
  // Count tasks completed by this team across all features
  let tasksCompleted = 0;
  for (const f of features) {
    const teamCounts = f.teams[teamName];
    if (teamCounts) {
      tasksCompleted += teamCounts.done;
    }
  }

  // Count features completed org-wide
  const completedFeatures = features.filter((f) => f.status === 'done' && f.completed_at_s != null);
  const featuresCompleted = completedFeatures.length;

  // Calculate earnings
  let earned = 0;
  let earningsPerSecond = 0;
  for (const f of completedFeatures) {
    const elapsed = currentTimeS - f.completed_at_s!;
    if (elapsed > 0) {
      earned += f.earnings_rate * elapsed;
    }
    earningsPerSecond += f.earnings_rate;
  }

  return { tasksCompleted, featuresCompleted, earned, earningsPerSecond };
}
